import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mailbox, Loader2, ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        <div className="mb-10 flex items-center gap-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary text-white">
            <Mailbox className="h-5 w-5" />
          </div>
          <div>
            <p className="font-heading text-xl font-bold tracking-tighter leading-none">MailShift</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Migration Engine</p>
          </div>
        </div>

        {sent ? (
          <div className="space-y-5">
            <MailCheck className="h-10 w-10 text-emerald-600" />
            <h1 className="font-heading text-3xl font-bold tracking-tighter">Check your email</h1>
            <p className="text-muted-foreground">
              If an account exists for <span className="font-mono">{email}</span>, we've sent a password reset link. It's valid for 1 hour.
            </p>
            <Link to="/login" className="inline-flex items-center gap-2 text-sm text-primary hover:underline" data-testid="back-to-login-link">
              <ArrowLeft className="h-4 w-4" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="font-heading text-4xl font-bold tracking-tighter">Forgot password?</h1>
            <p className="mt-3 text-muted-foreground">Enter your email and we'll send you a reset link.</p>

            <form onSubmit={submit} className="mt-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em]">Email</Label>
                <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="forgot-email-input" />
              </div>
              <Button type="submit" className="w-full gap-2" disabled={loading} data-testid="forgot-submit-button">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Send reset link
              </Button>
              <Link to="/login" className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="h-4 w-4" /> Back to sign in
              </Link>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
